import {
  Add as AddIcon,
  Edit as EditIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import CommissionGroupFormDialog from "../components/CommissionGroupFormDialog";
import MarketGrowthDataTable, {
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";

const CommissionGroupsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await MarketGrowthAdminApi.getGroups();
      const { error: err, data } = MarketGrowthAdminApi.normalizeList(
        res,
        "بارگذاری گروه‌ها ناموفق بود."
      );
      if (err) {
        setError(err);
        setItems([]);
        return;
      }
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tableRows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        statusLabel: row.isActive ? "فعال" : "غیرفعال",
      })),
    [items]
  );

  const headCells = useMemo(
    () => [
      { id: "title", label: "عنوان" },
      { id: "code", label: "کد" },
      { id: "statusLabel", label: "وضعیت", type: "chip" },
      { id: "validFrom", label: "تاریخ شروع", type: "date" },
      { id: "validTo", label: "تاریخ پایان", type: "date" },
    ],
    []
  );

  const openCreate = () => {
    setEditingItem(null);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingItem(row);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (!saving) {
      setDialogOpen(false);
      setEditingItem(null);
    }
  };

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      const res = editingItem
        ? await MarketGrowthAdminApi.updateGroup(editingItem.id, payload)
        : await MarketGrowthAdminApi.createGroup(payload);
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        editingItem ? "ویرایش گروه ناموفق بود." : "ایجاد گروه ناموفق بود."
      );
      if (normalized.error) {
        return { error: normalized.error };
      }
      toast.success(editingItem ? "گروه با موفقیت ویرایش شد." : "گروه با موفقیت ایجاد شد.");
      setDialogOpen(false);
      setEditingItem(null);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    setStatusUpdatingId(row.id);
    try {
      const res = await MarketGrowthAdminApi.patchGroupStatus(row.id, !row.isActive);
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        "تغییر وضعیت گروه ناموفق بود."
      );
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      toast.success(row.isActive ? "گروه غیرفعال شد." : "گروه فعال شد.");
      await load();
    } catch (e) {
      toast.error(e?.message || "خطا در تغییر وضعیت");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const tableActions = useMemo(
    () => [
      {
        icon: <EditIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: openEdit,
        color: "primary",
        tooltip: "ویرایش",
      },
      {
        icon: <PowerSettingsNewIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: toggleStatus,
        color: "secondary",
        tooltip: "تغییر وضعیت",
        hidden: (row) => statusUpdatingId === row.id,
      },
    ],
    [statusUpdatingId]
  );

  return (
    <MarketGrowthPageShell
      title="گروه‌های پورسانتی"
      description="مدیریت گروه‌های قوانین پورسانت."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          ایجاد گروه جدید
        </Button>
        <Tooltip title="بارگذاری مجدد">
          <IconButton onClick={load}>
            <SyncIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <MarketGrowthDataTable
            rowKey="id"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            defaultSort={{ orderBy: "title", order: "asc" }}
          />
        </CardContent>
      </Card>

      <CommissionGroupFormDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSubmit={handleSubmit}
        initialData={editingItem}
        saving={saving}
      />
    </MarketGrowthPageShell>
  );
};

export default CommissionGroupsPage;
